import React, { useState, useEffect } from "react";
import useMediaQuery from "hooks/useMediaQuery";
import { BreakPoints } from "utils/BreakPoints";
import styled from "styled-components";
import PropTypes from "prop-types";
import amplitude from "amplitude-js";
import { IoCallOutline } from "react-icons/io5";

interface LinkProps {
  readonly backgroundColor: string;
  readonly textColor: string;
  readonly variation?: boolean;
  readonly showPhone: boolean;
  readonly isAutism?: boolean;
  hasNumber?: boolean;
  hasObligationText?: boolean;
}
interface SeparatorProps {
  readonly separatorColor: string;
}
interface PhoneNumberProps {
  readonly variation?: boolean;
}

interface ShowPhone {
  readonly showPhone: boolean;
  readonly isAutism?: boolean;
  hasNumber?: boolean;
}

const CtaLink = styled.a<LinkProps>`
  display: inline-flex;
  height: 64px;
  width: ${(props) =>
    props.showPhone ? "231px" : props.hasNumber ? "237px" : "100%"};
  border-radius: 4px;
  text-decoration: none;
  margin-top: 8px;

  // padding-left: ${(props) => (!props.hasNumber ? "30px" : "0")};
  padding: 0px ${(props) => (!props.hasNumber ? "20px" : "20px")};

  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.textColor};
  font-size: 10px;
  justify-content: ${(props) => (!props.hasNumber ? "center" : "start")};
  text-transform: ${(props) => (props.showPhone ? "uppercase" : "uppercase")};
  @media only screen and (min-width: 768px) {
    font-size: 14px;
    height: 80px;
    width: auto;
  }
`;

const CtaLinkDiv = styled.a<LinkProps>`
  display: flex;
  height: 64px;
  width: ${(props) => (props.isAutism ? "255px" : "237px")};
  border-radius: 4px;
  text-decoration: none;
  margin-top: 8px;
  cursor: pointer;

  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.textColor};
  font-size: 10px;
  text-transform: uppercase;

  @media only screen and (min-width: 768px) {
    font-size: 14px;
    height: 80px;
    width: 282px;
  }
`;

const HeroCtaIcon = styled.div<ShowPhone>`
  width: ${(props) => (props.showPhone ? "auto" : "16px")};
  display: flex;
  padding-right: 10px;
  align-items: center;
  justify-content: center;
  @media only screen and (min-width: 768px) {
    width: auto;
  }
`;

const HeroCtaSeparator = styled.div<SeparatorProps>`
  width: 2px;
  background: ${(props) => props.separatorColor};
  opacity: ${(props) => (props.separatorColor === "#FFFFFF" ? "0.16" : "1")};
`;

const HeroCtaText = styled.div<ShowPhone>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 0px;
  font-size: ${(props) => (props.hasNumber ? "12px" : "20px")};
  line-height: 100%;
  text-align: left;
  font-weight: 600;
  position: relative;
  margin-top: 1px;

  @media only screen and (min-width: 768px) {
    font-size: 14px;
    padding-left: 0px;
  }
  @media only screen and (max-width: 768px) {
    font-size: 18px;
  }
  @media only screen and (max-width: 335px) {
    font-size: 14px;
  }
`;

const HeroCtaTextLink = styled.a<ShowPhone>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 10px;
  font-size: 20px;
  text-align: left;
  font-weight: 600;
  position: relative;
  color: white;
  text-decoration: none;

  @media only screen and (min-width: 768px) {
    font-size: 20px;
    padding-left: 20px;
    margin-bottom: 4px;
  }
`;

const HeroCtaNumber = styled.span<PhoneNumberProps>`
  font-size: 18px;
  line-height: 130%;
  font-weight: 600;
  letter-spacing: -0.01em;

  @media only screen and (min-width: 768px) {
    font-size: 22px;
    line-height: 32px;
  }
`;

const ObligationText = styled.span`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 135%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #094c61;
  width: 100%;
  padding: 14px 0;
`;

function BodyCallCta({
  text = "Free confidential call",
  phoneText = "",
  ctaPhoneLink = "",
  backgroundColor = "",
  separatorColor = "",
  hasNumber = true,
  textColor = "#fff",
  component = "",
  showPhone = true,
  hasObligationText = false,
  website = "",
  setAutismModalOpen,
  isAutism,
}): JSX.Element {
  const isTabletBreakPoint = useMediaQuery(BreakPoints.MOBILE);
  const [isTablet, setTablet] = useState(false);

  useEffect(() => {
    if (isTabletBreakPoint) {
      setTablet(true);
    } else {
      setTablet(false);
    }
  }, [isTabletBreakPoint]);

  const onClickHandler = (info: any) => {
    if (info === "Free Confidential Call") {
      setAutismModalOpen(true);
    }
    const API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;
    const PAGE = process.env.REACT_APP_WEBSITE;
    if (API_KEY && PAGE) {
      amplitude.getInstance().init(API_KEY);
      const event = "LandingPageButtonClick";
      const data = {
        page: website ? "Zinnia " + website : "Zinnia " + PAGE,
        website: window.location.pathname,
        action: "Button Click",
        text: info,
        component: component,
      };
      amplitude.getInstance().logEvent(event, data);
    }
  };

  return (
    <>
      {website === "Autism" ? (
        <CtaLinkDiv
          backgroundColor={backgroundColor}
          textColor={textColor}
          showPhone={showPhone}
          isAutism={isAutism}
          href={ctaPhoneLink}
          onClick={() => onClickHandler(text)}
        >
          <HeroCtaIcon showPhone={showPhone} isAutism={isAutism}>
            {showPhone && (
              <a href={ctaPhoneLink}>
                <img
                  src="https://cdn.zinniahealth.com/wp-content/uploads/20220323091413/TelephoneIcon.svg"
                  width={isTablet ? 24 : 28}
                  alt="phone icon"
                />
              </a>
            )}
          </HeroCtaIcon>
          <HeroCtaSeparator separatorColor={separatorColor} />

          {showPhone ? (
            <HeroCtaTextLink showPhone={showPhone} href={ctaPhoneLink}>
              <span>{text}</span>
              <HeroCtaNumber variation>{phoneText}</HeroCtaNumber>
            </HeroCtaTextLink>
          ) : (
            <HeroCtaText showPhone={showPhone}>
              <span>{text}</span>
              <HeroCtaNumber variation>{phoneText}</HeroCtaNumber>
            </HeroCtaText>
          )}
        </CtaLinkDiv>
      ) : (
        <>
          <CtaLink
            hasNumber={hasNumber}
            backgroundColor={backgroundColor}
            textColor={textColor}
            href={ctaPhoneLink}
            onClick={() => onClickHandler(text)}
            showPhone={false}
            className="main-button-cta"
          >
            {hasNumber && (
              <>
                <HeroCtaIcon showPhone={showPhone}>
                  {showPhone && <IoCallOutline size={32} />}
                </HeroCtaIcon>
              </>
            )}
            {!hasNumber && (
              <HeroCtaIcon showPhone={showPhone}>
                <IoCallOutline size={32} />
              </HeroCtaIcon>
            )}
            <HeroCtaText hasNumber={hasNumber} showPhone={showPhone}>
              <span className="main-button-cta-text">{text}</span>
              {hasNumber && (
                <HeroCtaNumber variation>{phoneText}</HeroCtaNumber>
              )}
            </HeroCtaText>
          </CtaLink>
          {hasObligationText && (
            <ObligationText>No obligation to enter treatment</ObligationText>
          )}
        </>
      )}
    </>
  );
}

BodyCallCta.propTypes = {
  text: PropTypes.string,
  phoneText: PropTypes.string,
  ctaPhoneLink: PropTypes.string,
  backgroundColor: PropTypes.string,
  separatorColor: PropTypes.string,
  textColor: PropTypes.string,
  variation: PropTypes.bool,
  showPhone: PropTypes.bool,
  website: PropTypes.string,
  setAutismModalOpen: PropTypes.func,
  isAutism: PropTypes.bool,
};

export default BodyCallCta;
